/**
 * <Avatar> — a circular user image with an initials fallback (atom).
 *
 * Renders a cropped circular photo from `uri`, or — when there's no image — a colored
 * circle with the person's initials (derived from `name`). Unlike `<ImageIcon>` (square,
 * `contain`, full-color illustrations), this is the round, cropped avatar used for tutors
 * and users.
 *
 * `signed` adds the **avatar signé** of Brand core V13 (chap. 2 / 11) : le nœud papillon centré
 * en bas, largeur 45 % de l'avatar, débordant sous le cercle, plus le liseré blanc qui le détache
 * du fond. Le nœud tient aussi sur le repli en initiales : c'est la signature de la maison, pas
 * une décoration de la photo.
 *
 * `variant="tutor"` est **la capsule** du livre V13 : photo ronde, liseré BLANC de 3 px, nœud
 * papillon JAUNE. Toujours la même, quel que soit le tuteur — la couleur du champion était un
 * code que la famille ne connaît pas, et la V13 la retire de la capsule (les couleurs de champion
 * restent des jetons, pour le dégradé des temps forts et `<Mascot>`). Il ne reste donc qu'un seul
 * nœud dans le DS, en aplat SVG : les quatre PNG rendus en 3D de l'ancien AvatarM sont ce que le
 * livre range dans les « jamais ».
 *
 * `selected` pose l'anneau de sélection dans la couleur de l'univers courant. Il ne retire jamais
 * le nœud — le livre l'écrit noir sur blanc.
 *
 * L'image vient de `uri` (serveur) ou de `source` (asset empaqueté, prioritaire) ; sans l'une ni
 * l'autre, ce sont les initiales — et aussi quand la photo du serveur ne charge pas : son adresse
 * signée expire (`hooks/useFailedImage`), et un disque vide ne dit plus qui c'est. Sans nom (un
 * visage qu'on ne nomme pas, `<AvatarStack>`), le repli est une silhouette neutre, jamais « ? ».
 *
 * `dimmed` l'estompe tout entier, nœud compris (jeton `dimming`) : le visage d'une séance où
 * l'enfant ne sera pas.
 *
 * No hex literals: every color comes from a semantic token.
 */
import React from "react"
import { Image, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { dimming } from "../../theme/tokens/effects"
import { type FontSizeKey } from "../../theme/tokens/typography"
import { useFailedImage } from "../../hooks/useFailedImage"
import { SignatureBow } from "./SignatureBow"
import { Icon } from "../icons/Icon"
import { Text } from "../typography/Text"

export type AvatarProps = {
    /** Image URL. When set, the photo is shown; otherwise the initials fallback renders. */
    uri?: string
    /**
     * Image empaquetée dans l'app (`import parent from ".../parent-512.png"`). Passe avant `uri` :
     * la pastille de l'espace parent dessine une image de l'app, pas une photo du serveur.
     */
    source?: number
    /** Full name — used to derive up to two initials for the fallback. */
    name?: string
    /** Diameter in px — defaults to 32. Le livre dessine l'avatar signé en 72 · 54 · 44 · 34. */
    size?: number
    /** Fallback circle background token — defaults to `initials-fill` (kit V16 : `#E3E8F6`). */
    backgroundColor?: SemanticColorKey
    /** Fallback initials color token — defaults to `primary`. */
    textColor?: SemanticColorKey
    /**
     * `tutor` → la capsule : liseré blanc 3 px + nœud jaune.
     * Defaults to `default` (plain circular photo / initials).
     */
    variant?: "default" | "tutor"
    /** Nœud papillon jaune signature. `variant="tutor"` le pose déjà. */
    signed?: boolean
    /** Anneau de sélection dans la couleur de l'univers. N'enlève jamais le nœud. */
    selected?: boolean
    /** Estompé : le visage d'une séance où l'enfant ne sera pas. Défaut `false`. */
    dimmed?: boolean
    /**
     * Un anneau dans ce jeton, sans dire « sélectionné » : il désigne un visage par sa couleur
     * (l'« Espace parent » du choix du compte, `role-parent`). Absent → pas d'anneau.
     */
    ringColor?: SemanticColorKey
    testID?: string
}

/** Épaisseur de l'anneau de sélection (chap. 11) — et de l'anneau de couleur (`ringColor`). */
const SELECTION_RING = 3

/** Liseré blanc de la capsule du tuteur — 3 px, quelle que soit la taille (V13). `<ChampionAvatar>` porte le même. */
export const TUTOR_HALO = 3

/** Liseré de l'avatar signé — 4 px à 72, 3 px à 54, 2 px en dessous (table du livre). */
function haloWidthFor(size: number): number {
    return Math.max(2, Math.round(size / 18))
}

/** "Sarah Dupont" → "SD", "Sarah" → "S". Appelé seulement avec un nom : sans lui, la silhouette. */
function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/)
    const first = parts[0]![0] ?? ""
    const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : ""
    return (first + last).toUpperCase()
}

/**
 * L'initiale du repli suit le visage : quatre marches, la dernière pour un visage en grand (le choix du compte).
 * Sous 32 px, la plus petite (daily du 04/11 : dans une vignette du live, l'initiale débordait de son visage).
 */
function initialsFontSize(size: number): FontSizeKey {
    if (size >= 96) return "text-4xl"
    if (size >= 40) return "text-lg"
    return size >= 32 ? "text-sm" : "text-xs"
}

export function Avatar(props: AvatarProps) {
    const {
        uri,
        source,
        name,
        size = 32,
        backgroundColor = "initials-fill",
        textColor = "primary",
        variant = "default",
        signed = false,
        selected = false,
        dimmed = false,
        ringColor,
        testID,
    } = props

    const { theme } = useUnistyles()

    const isTutor = variant === "tutor"
    const showBow = signed || isTutor
    // Le liseré blanc : 3 px fixes pour la capsule du tuteur, la table du livre pour l'avatar signé.
    const halo = isTutor ? TUTOR_HALO : showBow ? haloWidthFor(size) : 0

    // La sélection passe devant : elle dit un état, l'anneau de couleur une identité.
    const ring = selected ? "role" : ringColor

    const photo = useFailedImage(uri || undefined)
    const imageSource = source != null ? source : uri && !photo.failed ? { uri } : null

    const face =
        imageSource != null ? (
            <Image source={imageSource} style={styles.image(size, halo)} onError={photo.onError} />
        ) : (
            <View style={styles.fallback(size, backgroundColor, halo)}>
                {name != null && name.trim() !== "" ? (
                    <Text size={initialsFontSize(size)} weight="bold" color={textColor}>
                        {initialsOf(name)}
                    </Text>
                ) : (
                    <Icon name="user-alt" size={size >= 40 ? 20 : 16} color="text-muted" />
                )}
            </View>
        )

    return (
        <View testID={testID} style={[styles.box(size), dimmed ? styles.dimmed : undefined]}>
            {face}
            {ring != null && <View style={styles.ring(size, SELECTION_RING, ring)} pointerEvents="none" />}
            {showBow && <SignatureBow size={size} color={theme.colors.accent} shade={theme.colors["accent-strong"]} />}
        </View>
    )
}

/**
 * Le liseré de la capsule est **blanc**, pas la surface posée du thème : le livre l'écrit ainsi
 * (« liseré blanc 3 px », chap. 11), et sur la nuit une bordure `surface-raised` se confondrait
 * avec la carte qui la porte — la capsule n'aurait plus de contour.
 */
const styles = StyleSheet.create((theme) => ({
    box: (size: number) => ({
        width: size,
        height: size,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
    image: (size: number, halo: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: halo,
        borderColor: theme.colors.white,
    }),
    fallback: (size: number, backgroundColor: SemanticColorKey, halo: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors[backgroundColor],
        borderWidth: halo,
        borderColor: theme.colors.white,
    }),
    dimmed: {
        opacity: dimming.opacity,
    },
    ring: (size: number, borderWidth: number, color: SemanticColorKey) => ({
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth,
        borderColor: theme.colors[color],
    }),
}))
